import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { PageBody, PageHeader, ScopeBar } from '../components/AppShell';
import {
  Button,
  Card,
  CardHeader,
  Field,
  Icon,
  Loading,
  Pill,
} from '../components/ui';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { dateTime } from '../lib/format';
import type {
  ActiveSession,
  EnrolmentStart,
  MfaMethod,
  MfaSummary,
} from '../lib/types';
import './shared.css';

const METHOD_NAME: Record<MfaMethod, string> = {
  totp: 'Authenticator app',
  sms: 'Text message',
  email: 'Email code',
};

const METHOD_BLURB: Record<MfaMethod, string> = {
  totp: 'A code from an app on your phone. Works with no signal, and cannot be intercepted by someone taking over your phone number.',
  sms: 'A code by text. Convenient, but a number can be moved to another SIM, so keep it as a fallback rather than your only factor.',
  email: 'A code to your inbox. Useful when you have no phone to hand — though it is only as strong as your mailbox.',
};

/**
 * Adding one second factor.
 *
 * Shared by the security screen and the setup screen a new account is held on,
 * because they are the same job: prove the factor reaches you, then keep it.
 */
export function AddFactor({
  method,
  onDone,
  onCancel,
}: {
  method: MfaMethod;
  onDone: (recoveryCodes?: string[]) => void;
  onCancel: () => void;
}) {
  const [start, setStart] = useState<EnrolmentStart | null>(null);
  const [target, setTarget] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);

  const begin = useCallback(
    async (dest?: string) => {
      setBusy(true);
      setError(null);
      try {
        setStart(
          await api.post<EnrolmentStart>('/auth/mfa/enrol', {
            method,
            ...(dest ? { target: dest } : {}),
          }),
        );
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not work.');
      } finally {
        setBusy(false);
      }
    },
    [method],
  );

  // An authenticator app needs no destination, so it starts immediately.
  useEffect(() => {
    if (method === 'totp') void begin();
  }, [method, begin]);

  async function confirm(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ recoveryCodes?: string[] }>('/auth/mfa/confirm', {
        method,
        code,
      });
      onDone(res.recoveryCodes);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  // SMS and email ask where to send before anything is sent.
  if (method !== 'totp' && !start) {
    return (
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void begin(target || undefined);
        }}
      >
        <p className="muted" style={{ margin: 0 }}>
          {METHOD_BLURB[method]}
        </p>
        <Field
          label={method === 'sms' ? 'Mobile number' : 'Email address'}
          hint={
            method === 'sms'
              ? 'International format, for example +1 404 555 0142.'
              : 'Leave blank to use the address you sign in with.'
          }
          htmlFor="factor-target"
        >
          <input
            id="factor-target"
            className="input"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder={method === 'sms' ? '+1 404 555 0142' : 'you@company.com'}
          />
        </Field>
        {error && (
          <div className="notice notice--danger" role="alert">
            {Icon.alert({ size: 15 })}
            <span>{error}</span>
          </div>
        )}
        <div className="row">
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? 'Sending…' : 'Send a code'}
          </Button>
          <Button type="button" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form className="stack" onSubmit={confirm}>
      {method === 'totp' ? (
        <>
          <p className="muted" style={{ margin: 0 }}>
            Scan this with 1Password, Google Authenticator, Authy or any other
            authenticator app, then enter the six-digit code it shows.
          </p>
          <div className="notice notice--warn">
            {Icon.alert({ size: 15 })}
            <span>
              If your app already holds a Fairmount Payments entry from an
              earlier attempt, delete it first. Only the newest one works, and a
              code read from an old entry looks exactly like a wrong code.
            </span>
          </div>
          {start?.qrDataUrl && (
            <img
              className="qr"
              src={start.qrDataUrl}
              alt="QR code for your authenticator app"
              width={190}
              height={190}
            />
          )}
          <button
            type="button"
            className="linkbtn"
            onClick={() => setShowSecret((v) => !v)}
          >
            {showSecret ? 'Hide the setup key' : 'Cannot scan? Show the setup key'}
          </button>
          {showSecret && <code className="secretkey">{start?.secret}</code>}
        </>
      ) : (
        <p className="muted" style={{ margin: 0 }}>
          We sent a code to <strong>{start?.sentTo}</strong>. Enter it below to
          confirm this factor.
        </p>
      )}

      {start?.devCode && (
        <div className="notice notice--info">
          {Icon.alert({ size: 15 })}
          <span>
            Development build — nothing was actually sent. The code is{' '}
            <strong className="mono">{start.devCode}</strong>.
          </span>
        </div>
      )}

      {error && (
        <div className="notice notice--danger" role="alert">
          {Icon.alert({ size: 15 })}
          <span>{error}</span>
        </div>
      )}

      <Field label="Six-digit code" htmlFor="factor-code">
        <input
          id="factor-code"
          className="input mono"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          placeholder="000000"
        />
      </Field>

      <div className="row">
        <Button type="submit" variant="primary" disabled={busy || code.length !== 6}>
          {busy ? 'Confirming…' : 'Confirm'}
        </Button>
        <Button type="button" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/** Shown once. Nothing can retrieve these afterwards — only replace them. */
export function RecoveryCodes({
  codes,
  onAcknowledge,
}: {
  codes: string[];
  onAcknowledge: () => void;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="stack">
      <p className="muted" style={{ margin: 0 }}>
        Save these somewhere safe — a password manager, or printed and locked
        away. Each one signs you in once if you lose your phone. This is the only
        time they are shown: we keep hashes, not the codes.
      </p>
      <ul className="codegrid">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <div className="row">
        <Button
          onClick={() => {
            void navigator.clipboard?.writeText(codes.join('\n'));
            setCopied(true);
          }}
        >
          {copied ? 'Copied' : 'Copy all'}
        </Button>
        <Button variant="primary" onClick={onAcknowledge}>
          I have saved them
        </Button>
      </div>
    </div>
  );
}

export function Security() {
  const { user, refreshUser } = useAuth();
  const [mfa, setMfa] = useState<MfaSummary | null>(null);
  const [sessions, setSessions] = useState<ActiveSession[] | null>(null);
  const [adding, setAdding] = useState<MfaMethod | null>(null);
  const [newCodes, setNewCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [summary, live] = await Promise.all([
      api.get<MfaSummary>('/auth/mfa'),
      api.get<ActiveSession[]>('/auth/sessions').catch(() => []),
    ]);
    setMfa(summary);
    setSessions(live);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(work: Promise<unknown>, done?: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await work;
      await load();
      await refreshUser();
      if (done) setNotice(done);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  if (!mfa) return <Loading label="Loading your security settings…" />;

  const held = (['totp', 'sms', 'email'] as MfaMethod[]).filter(
    (m) => mfa.methods[m],
  );

  return (
    <>
      <PageHeader
        breadcrumb={['Your account', 'Security']}
        title="Security"
        actions={
          <Pill tone={mfa.enabled ? 'ok' : 'danger'}>
            {mfa.enabled ? `${held.length} factor${held.length === 1 ? '' : 's'}` : 'No second factor'}
          </Pill>
        }
      />

      <ScopeBar>
        Signed in as <strong>{user?.email}</strong>. Changing your password signs
        every device out, including this one.
      </ScopeBar>

      <PageBody>
        {error && (
          <div className="notice notice--danger" role="alert">
            {Icon.alert({ size: 15 })}
            <span>{error}</span>
          </div>
        )}
        {notice && (
          <div className="notice notice--ok" role="status">
            {Icon.check({ size: 15 })}
            <span>{notice}</span>
          </div>
        )}

        <div className="split-2">
          <div className="stack">
            <Card>
              <CardHeader
                title="Two-factor authentication"
                meta={
                  mfa.preferred
                    ? `${METHOD_NAME[mfa.preferred]} is offered first`
                    : undefined
                }
              />

              {newCodes ? (
                <RecoveryCodes
                  codes={newCodes}
                  onAcknowledge={() => {
                    setNewCodes(null);
                    setAdding(null);
                  }}
                />
              ) : adding ? (
                <AddFactor
                  method={adding}
                  onCancel={() => setAdding(null)}
                  onDone={(codes) => {
                    setAdding(null);
                    if (codes) setNewCodes(codes);
                    void run(Promise.resolve(), `${METHOD_NAME[adding]} added.`);
                  }}
                />
              ) : (
                <div className="stack" style={{ gap: 14 }}>
                  {(['totp', 'sms', 'email'] as MfaMethod[]).map((method) => {
                    const enrolled = mfa.methods[method];
                    const usable = mfa.available[method];
                    return (
                      <div key={method} className="factor">
                        <div className="factor__head">
                          <strong>{METHOD_NAME[method]}</strong>
                          {enrolled ? (
                            <Pill tone="ok">On</Pill>
                          ) : usable ? (
                            <Pill tone="neutral">Off</Pill>
                          ) : (
                            <Pill tone="warn">Unavailable</Pill>
                          )}
                        </div>
                        <p className="factor__blurb">{METHOD_BLURB[method]}</p>
                        {enrolled && 'sentTo' in enrolled && enrolled.sentTo && (
                          <p className="factor__blurb">
                            Sends to <strong>{enrolled.sentTo}</strong>
                          </p>
                        )}
                        <div className="row">
                          {enrolled ? (
                            <>
                              {mfa.preferred !== method && (
                                <Button
                                  disabled={busy}
                                  onClick={() =>
                                    void run(
                                      api.post('/auth/mfa/preferred', { method }),
                                      `${METHOD_NAME[method]} will be offered first.`,
                                    )
                                  }
                                >
                                  Offer this first
                                </Button>
                              )}
                              <Button
                                disabled={busy || held.length === 1}
                                onClick={() =>
                                  void run(
                                    api.del(`/auth/mfa/${method}`),
                                    `${METHOD_NAME[method]} removed.`,
                                  )
                                }
                              >
                                Remove
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant={held.length === 0 ? 'primary' : undefined}
                              disabled={busy || !usable}
                              onClick={() => setAdding(method)}
                            >
                              {usable ? 'Set up' : 'Not configured on this server'}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            <Card>
              <CardHeader
                title="Recovery codes"
                meta={`${mfa.recoveryCodesRemaining} unused`}
              />
              <div className="stack">
                <p className="muted" style={{ margin: 0 }}>
                  Your way back in if you lose every factor above. Reissuing
                  replaces the whole set, including codes you have not used.
                </p>
                <div className="row">
                  <Button
                    disabled={busy || !mfa.enabled}
                    onClick={() =>
                      void (async () => {
                        setBusy(true);
                        try {
                          const res = await api.post<{ codes: string[] }>(
                            '/auth/mfa/recovery-codes',
                          );
                          setNewCodes(res.codes);
                          await load();
                        } catch (err) {
                          setError(
                            err instanceof ApiError ? err.message : 'That did not work.',
                          );
                        } finally {
                          setBusy(false);
                        }
                      })()
                    }
                  >
                    Issue a new set
                  </Button>
                </div>
              </div>
            </Card>
          </div>

          <div className="stack">
            <ChangePassword onDone={(m) => setNotice(m)} />

            <Card>
              <CardHeader
                title="Where you are signed in"
                count={sessions?.length ?? 0}
              />
              <div className="stack" style={{ gap: 12 }}>
                {(sessions ?? []).map((s) => (
                  <div key={s.sessionId} className="factor">
                    <div className="factor__head">
                      <strong>{describeAgent(s.userAgent)}</strong>
                      {s.current && <Pill tone="ok">This device</Pill>}
                    </div>
                    <p className="factor__blurb">
                      {s.ip ?? 'unknown address'} · last used {dateTime(s.lastUsedAt)}
                      {s.mfaMethod ? ` · cleared with ${METHOD_NAME[s.mfaMethod].toLowerCase()}` : ''}
                    </p>
                    {!s.current && (
                      <div className="row">
                        <Button
                          disabled={busy}
                          onClick={() =>
                            void run(
                              api.del(`/auth/sessions/${s.sessionId}`),
                              'That session was ended.',
                            )
                          }
                        >
                          Sign it out
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
                <div className="row">
                  <Button
                    disabled={busy}
                    onClick={() => void run(api.post('/auth/sessions/revoke-all'))}
                  >
                    Sign out everywhere
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </PageBody>
    </>
  );
}

function ChangePassword({ onDone }: { onDone: (message: string) => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/password/change', {
        currentPassword: current,
        newPassword: next,
      });
      onDone('Password changed. Every device has been signed out.');
      setCurrent('');
      setNext('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader title="Password" />
      <form className="stack" onSubmit={submit}>
        <Field label="Current password" htmlFor="pw-current">
          <input
            id="pw-current"
            className="input"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </Field>
        <Field
          label="New password"
          hint="A few unrelated words beat a short password with symbols in it. Checked against known breaches."
          htmlFor="pw-next"
        >
          <input
            id="pw-next"
            className="input"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        </Field>
        {error && (
          <div className="notice notice--danger" role="alert">
            {Icon.alert({ size: 15 })}
            <span>{error}</span>
          </div>
        )}
        <div className="row">
          <Button type="submit" variant="primary" disabled={busy || !current || !next}>
            {busy ? 'Changing…' : 'Change password'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** A browser and platform, rather than a hundred characters of user agent. */
function describeAgent(ua?: string): string {
  if (!ua) return 'Unknown device';
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua)
      ? 'Chrome'
      : /Safari\//.test(ua)
        ? 'Safari'
        : /Firefox\//.test(ua)
          ? 'Firefox'
          : /curl/i.test(ua)
            ? 'curl'
            : 'Browser';
  const platform = /iPhone|iPad/.test(ua)
    ? 'iOS'
    : /Android/.test(ua)
      ? 'Android'
      : /Mac OS X/.test(ua)
        ? 'macOS'
        : /Windows/.test(ua)
          ? 'Windows'
          : /Linux/.test(ua)
            ? 'Linux'
            : '';
  return platform ? `${browser} on ${platform}` : browser;
}

export { METHOD_NAME };
